import { use_page_scroll_state } from '@/hook/use_page_scroll_state';
import {
  PORTFOLIO_NAVIGATION_ITEMS,
  get_active_navigation_section_id,
  scroll_to_navigation_item,
} from '@/lib/portfolio-navigation';
import type {
  AssetIndexBySequenceNumber,
  PortfolioNavigationItem,
} from '@/lib/portfolio-navigation';
import { cn } from '@/lib/utils';

type PortfolioBottomNavigationProps = {
  asset_index_by_sequence_number: AssetIndexBySequenceNumber;
  current_asset_sequence_number: number;
};

const PORTFOLIO_BOTTOM_NAVIGATION_CLASS_NAME =
  'pointer-events-none fixed inset-x-0 bottom-0 z-50 flex select-none justify-center px-3 pb-[calc(env(safe-area-inset-bottom)+14px)]';
const PORTFOLIO_BOTTOM_NAVIGATION_LIST_CLASS_NAME =
  'flex h-11 w-[min(calc(100vw-24px),346px)] select-none items-center rounded-[22px] bg-[#fffcf2]/95 px-2 shadow-[0_4px_18px_rgba(60,49,32,0.16)] backdrop-blur transition-opacity ease-out';
const PORTFOLIO_BOTTOM_NAVIGATION_BUTTON_CLASS_NAME =
  'h-full min-w-0 flex-1 touch-manipulation select-none border-0 bg-transparent px-0 text-center text-[13px] leading-none tracking-normal text-[#67645d] transition-colors hover:cursor-pointer hover:font-bold hover:text-[#26221c] focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-[-2px] focus-visible:outline-[#d7d0c5]';
const PORTFOLIO_BOTTOM_NAVIGATION_BUTTON_ACTIVE_CLASS_NAME =
  'font-bold text-[#26221c]';
const PORTFOLIO_BOTTOM_NAVIGATION_BUTTON_INACTIVE_CLASS_NAME = 'font-medium';
const PORTFOLIO_BOTTOM_NAVIGATION_SEPARATOR_CLASS_NAME =
  'h-4 w-px shrink-0 bg-[#d7d0c5]';

export function PortfolioBottomNavigation({
  asset_index_by_sequence_number,
  current_asset_sequence_number,
}: PortfolioBottomNavigationProps) {
  const is_page_scrolling = use_page_scroll_state();
  const active_navigation_section_id = get_active_navigation_section_id(
    current_asset_sequence_number,
  );

  return (
    <nav
      aria-label="作品分类导航"
      className={PORTFOLIO_BOTTOM_NAVIGATION_CLASS_NAME}
    >
      <div className={get_navigation_list_class_name({ is_page_scrolling })}>
        {PORTFOLIO_NAVIGATION_ITEMS.map((navigation_item, navigation_index) => {
          const is_navigation_item_active =
            navigation_item.section_id === active_navigation_section_id;

          return (
            <NavigationItemButton
              key={navigation_item.section_id}
              asset_index_by_sequence_number={asset_index_by_sequence_number}
              is_navigation_item_active={is_navigation_item_active}
              navigation_index={navigation_index}
              navigation_item={navigation_item}
            />
          );
        })}
      </div>
    </nav>
  );
}

function get_navigation_list_class_name({
  is_page_scrolling,
}: {
  is_page_scrolling: boolean;
}) {
  return cn(
    PORTFOLIO_BOTTOM_NAVIGATION_LIST_CLASS_NAME,
    is_page_scrolling && 'pointer-events-none opacity-0 duration-300',
    !is_page_scrolling &&
      'pointer-events-auto opacity-80 duration-[520ms] hover:opacity-100',
  );
}

function NavigationItemButton({
  asset_index_by_sequence_number,
  is_navigation_item_active,
  navigation_index,
  navigation_item,
}: {
  asset_index_by_sequence_number: AssetIndexBySequenceNumber;
  is_navigation_item_active: boolean;
  navigation_index: number;
  navigation_item: PortfolioNavigationItem;
}) {
  return (
    <>
      {navigation_index > 0 ?
        <span
          aria-hidden="true"
          className={PORTFOLIO_BOTTOM_NAVIGATION_SEPARATOR_CLASS_NAME}
        />
      : null}
      <button
        aria-current={is_navigation_item_active ? 'page' : undefined}
        className={get_navigation_button_class_name(is_navigation_item_active)}
        onClick={() =>
          scroll_to_navigation_item({
            asset_index_by_sequence_number,
            navigation_item,
          })
        }
        type="button"
      >
        {navigation_item.label}
      </button>
    </>
  );
}

function get_navigation_button_class_name(is_navigation_item_active: boolean) {
  return cn(
    PORTFOLIO_BOTTOM_NAVIGATION_BUTTON_CLASS_NAME,
    is_navigation_item_active ?
      PORTFOLIO_BOTTOM_NAVIGATION_BUTTON_ACTIVE_CLASS_NAME
    : PORTFOLIO_BOTTOM_NAVIGATION_BUTTON_INACTIVE_CLASS_NAME,
  );
}
